import * as Yup from 'yup';
import { IndividualProfile } from '../components/profile/fo-profile-individual-drawer';

// let falseValue;

// Base schema (common for both scenarios)
const baseProfileSchema = {
  salutation: Yup.object({
    salutation: Yup.string().required('Salutation is required'),
  })
    .nullable()
    .default(null)
    .label('Salutation'),
  firstName: Yup.string().default('').typeError('First Name is required'),
  middleName: Yup.string().default(''),
  lastName: Yup.string()
    .default('')
    .typeError('Last Name is required')
    .required(),
  nationality: Yup.object({
    code: Yup.string().required('Code is required'),
    caption: Yup.string().required('Caption is required'),
  })
    .default(null)
    .nullable()
    .required()
    .label('Nationality'),
  gender: Yup.string().default(null).typeError('Gender').notRequired(),
  birthday: Yup.object().default(null).label('Birthday').notRequired(),
  weddingAnniv: Yup.object()
    .default(null)
    .label('Wedding Anniversary')
    .notRequired(),
  contactNumber: Yup.string().typeError('Contact Number').required(),
  primaryEmail: Yup.string()
    .email('Please enter a valid email address')
    .test({
      name: 'isEmailAddress',
      skipAbsent: true,
      test(value, ctx) {
        if (!value) {
          return true;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
          return ctx.createError({ message: 'Please enter a valid email' });
        }

        return true;
      },
    })
    .label('Primary Email is required'),
  marketSegment: Yup.string()
    .default('')
    .transform((value) => (value === null ? undefined : value))
    .notRequired(),
  businessSource: Yup.string()
    // .when('marketSegment', (marketSegment, schema) => {
    //   [falseValue] = marketSegment;
    //   if (falseValue) return schema.required('Business Source is required');
    //   return schema;
    // })
    .notRequired()
    .default('')
    .label('Business Source'),
  sms: Yup.boolean().default(false).label('SMS'),
  email: Yup.boolean().default(false).label('Email'),
  vipLevel: Yup.object({
    vip_level: Yup.string().required('VIP is required'),
  })
    .default(null)
    .nullable(),
  accountOwner: Yup.string().typeError('Account Owner').notRequired(),
  homeAddress: Yup.string().typeError('Home Address').notRequired(),
  cityAddress: Yup.string().typeError('City/Province').notRequired(),
  country: Yup.object({
    code: Yup.string().required('Code is required'),
    caption: Yup.string().required('Caption is required'),
  })
    .default(null)
    .nullable()
    .required()
    .label('Country'),
  company: Yup.string().typeError('Company').notRequired(),
  department: Yup.string().typeError('Department').notRequired(),
  designation: Yup.string().typeError('Designation').notRequired(),
  guestRemarks: Yup.string().default('').label('Guest Remarks').notRequired(),
  ageGroup: Yup.string().default(null).typeError('Age Group').notRequired(),
  hotel: Yup.boolean().default(false).label('Hotel'),
  pos: Yup.boolean().default(false).label('POS'),
  banquet: Yup.boolean().default(false).label('Banquet'),
  athlete: Yup.boolean().default(false).label('Athlete'),
  pwd: Yup.boolean().default(false).label('PWD'),
  blacklisted: Yup.boolean().default(false).label('Blacklisted'),
  accountMapping: Yup.string()
    .default('')
    .label('Account Mapping')
    .notRequired(),
  vatType: Yup.string().default('').label('VAT Type').required(),
  creditLimit: Yup.number().default(0).notRequired(),
  creditTerms: Yup.number().default(0).notRequired(),
  tinNo: Yup.string()
    .default('')
    .label('Tax Identification Number')
    .notRequired(),
  taxCode: Yup.string().default('').label('Tax Code').notRequired(),
  businessStyle: Yup.string().default('').label('Business Style').notRequired(),
  salesToGov: Yup.boolean().default(false).label('Sales to Government'),
};

// Main schema function with conditional
export const profileIndividualSchema = (withLockset: boolean) => {
  // Add conditional required/notRequired to email

  const schema = Yup.object().shape({
    ...baseProfileSchema,
    primaryEmail: withLockset
      ? baseProfileSchema?.primaryEmail.required('Primary Email is required')
      : baseProfileSchema?.primaryEmail.notRequired(),
  });

  return schema as Yup.ObjectSchema<IndividualProfile>;
};

export const profileGroupSchema = Yup.object().shape({
  companyName: Yup.string()
    .default('')
    .typeError('Full Name is required')
    .required(),
  contactNumber: Yup.string().typeError('Contact Number').required(),
  primaryEmail: Yup.string()
    .email('Please enter a valid email address')
    .test({
      name: 'isEmailAddress',
      skipAbsent: true,
      test(value, ctx) {
        if (!value) {
          return true;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
          return ctx.createError({ message: 'Please enter a valid email' });
        }

        return true;
      },
    })
    .label('Primary Email is required')
    .required(),
  marketSegment: Yup.string()
    .default('')
    .transform((value) => (value === null ? undefined : value))
    .notRequired(),
  businessSource: Yup.string()
    // .when('marketSegment', (marketSegment, schema) => {
    //   [falseValue] = marketSegment;
    //   if (falseValue) return schema.required('Business Source is required');
    //   return schema;
    // })
    .notRequired()
    .default('')
    .label('Business Source'),
  sms: Yup.boolean().default(false).label('SMS'),
  email: Yup.boolean().default(false).label('Email'),
  vipLevel: Yup.object({
    vip_level: Yup.string().required('Caption is required'),
  })
    .default(null)
    .nullable(),
  accountOwner: Yup.string().typeError('Account Owner').notRequired(),
  homeAddress: Yup.string().typeError('Home Address').notRequired(),
  cityAddress: Yup.string().typeError('City/Province').notRequired(),
  country: Yup.object({
    code: Yup.string().required('Code is required'),
    caption: Yup.string().required('Caption is required'),
  })
    .default(null)
    .nullable()
    .required()
    .label('Country'),
  hotel: Yup.boolean().default(false).label('Hotel'),
  pos: Yup.boolean().default(false).label('POS'),
  banquet: Yup.boolean().default(false).label('banquet'),
  blacklisted: Yup.boolean().default(false).label('Blacklisted'),
  accountMapping: Yup.string()
    .default('')
    .label('Account Mapping')
    .notRequired(),
  vatType: Yup.string().default('').label('VAT Type').notRequired(),
  creditLimit: Yup.number().default(0).notRequired(),
  creditTerms: Yup.number().default(0).notRequired(),
  guestRemarks: Yup.string().default('').label('Guest Remarks').notRequired(),
  tinNo: Yup.string()
    .default('')
    .label('Tax Identification Number')
    .notRequired(),
  taxCode: Yup.string().default('').label('Tax Code').notRequired(),
  businessStyle: Yup.string().default('').label('Business Style').notRequired(),
  salesToGov: Yup.boolean().default(false).label('Sales to Goverment'),
});

export const profileAddContactNumberSchema = Yup.object().shape({
  contactNumber: Yup.string().typeError('Contact Number').required(),
  type: Yup.string().typeError('Type').required(),
  primary: Yup.string().typeError('Is Primary').required(),
});

export const profileAddEmailSchema = Yup.object().shape({
  email: Yup.string().typeError('Email').required(),
  primary: Yup.string().typeError('Is Primary').required(),
});
